'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  useOptionalAccessControlState,
  useOptionalAdminStatus,
  useOptionalAppShell,
} from '@/components/shell/AppShellContext';
import type { Role } from '@/lib/access-control/types';
import { hasPermission as checkHasPermission } from '@/lib/access-control/permissions';
import logger from '@/lib/logger';
import { dedupedFetchJson, invalidateDedupedFetch } from '@/lib/utils/dedupedFetch';

const MASQUERADE_URL = '/api/access-control/masquerade';

export interface UseMasqueradeResult {
  /** Whether masquerade is currently active */
  isActive: boolean;
  /** Role IDs being masqueraded */
  roleIds: string[];
  /** Role names being masqueraded */
  roleNames: string[];
  /** Whether the user has permission to masquerade */
  canMasquerade: boolean;
  /** Roles available for masquerading (at or below user's privilege level) */
  availableRoles: Role[];
  /** Activate masquerade with selected role IDs */
  activateMasquerade: (roleIds: string[]) => Promise<void>;
  /** End masquerade and restore original permissions */
  deactivateMasquerade: () => Promise<void>;
  /** Loading state */
  loading: boolean;
}

export function useMasquerade(): UseMasqueradeResult {
  const appShell = useOptionalAppShell();
  const accessControlState = useOptionalAccessControlState();
  const adminStatus = useOptionalAdminStatus();

  const [fallbackState, setFallbackState] = useState<{
    isActive: boolean;
    roleIds: string[];
    roleNames: string[];
  }>({
    isActive: false,
    roleIds: [],
    roleNames: [],
  });
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);

  const usingSharedState = Boolean(accessControlState);
  const isActive = usingSharedState
    ? Boolean(accessControlState?.isMasquerading)
    : fallbackState.isActive;
  const roleIds = usingSharedState
    ? accessControlState?.masqueradeRoleIds || []
    : fallbackState.roleIds;
  const sharedRoleNames = usingSharedState
    ? accessControlState?.masqueradeRoleNames || []
    : fallbackState.roleNames;

  const canMasquerade =
    Boolean(adminStatus?.isPlatformAdmin) ||
    checkHasPermission(accessControlState?.userContext || null, 'roles.masquerade');

  useEffect(() => {
    if (!canMasquerade) {
      setAvailableRoles([]);
      return;
    }

    let cancelled = false;

    async function fetchRoles() {
      try {
        const data = await dedupedFetchJson<{ roles?: any[] }>(MASQUERADE_URL, {
          throwOnError: false,
        });
        if (cancelled) return;

        const roles: Role[] = (data?.roles || []).map((role: any) => ({
          id: role.id,
          tenant_org_unit_id: role.tenant_org_unit_id ?? null,
          name: role.name,
          slug: role.slug || '',
          description: role.description ?? null,
          privilege_level: role.privilege_level ?? 0,
          is_system_role: role.is_system_role ?? false,
          is_assignable: role.is_assignable ?? true,
          is_default: role.is_default ?? false,
          color: role.color ?? null,
          icon: role.icon ?? null,
          badge_url: role.badge_url ?? null,
          created_at: role.created_at || '',
          updated_at: role.updated_at || '',
        }));

        setAvailableRoles(roles);
      } catch (error) {
        logger.warn('useMasquerade', 'Failed to fetch available roles', { error });
      }
    }

    fetchRoles();

    return () => {
      cancelled = true;
    };
  }, [canMasquerade]);

  const activateMasquerade = useCallback(async (selectedRoleIds: string[]) => {
    if (!canMasquerade || selectedRoleIds.length === 0) return;

    setLoading(true);
    try {
      const response = await fetch('/api/access-control/masquerade', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role_ids: selectedRoleIds }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to activate masquerade');
      }

      const data = await response.json();

      if (appShell) {
        await appShell.refetchPermissions();
      } else {
        setFallbackState({
          isActive: true,
          roleIds: data.role_ids,
          roleNames: data.role_names,
        });
      }

      // Invalidate caches and signal NavigationProvider to refetch
      invalidateDedupedFetch(MASQUERADE_URL);
      localStorage.removeItem('neutralaiz-navigation-cache');
      window.dispatchEvent(new CustomEvent('masquerade-changed'));

      logger.info('useMasquerade', 'Masquerade activated', {
        roleIds: data.role_ids,
        roleNames: data.role_names,
      });
    } catch (error) {
      logger.error('useMasquerade', 'Failed to activate masquerade', { error });
      throw error;
    } finally {
      setLoading(false);
    }
  }, [appShell, canMasquerade]);

  const deactivateMasquerade = useCallback(async () => {
    setLoading(true);
    try {
      await fetch('/api/access-control/masquerade', {
        method: 'DELETE',
        credentials: 'include',
      });

      if (appShell) {
        await appShell.refetchPermissions();
      } else {
        setFallbackState({
          isActive: false,
          roleIds: [],
          roleNames: [],
        });
      }

      // Invalidate caches and signal NavigationProvider to refetch
      invalidateDedupedFetch(MASQUERADE_URL);
      localStorage.removeItem('neutralaiz-navigation-cache');
      window.dispatchEvent(new CustomEvent('masquerade-changed'));

      logger.info('useMasquerade', 'Masquerade deactivated');
    } catch (error) {
      logger.error('useMasquerade', 'Failed to deactivate masquerade', { error });
    } finally {
      setLoading(false);
    }
  }, [appShell]);

  const roleNames = useMemo(() => {
    if (sharedRoleNames.length > 0) {
      return sharedRoleNames;
    }

    if (roleIds.length === 0) {
      return [];
    }

    const roleNameById = new Map(availableRoles.map((role) => [role.id, role.name]));
    return roleIds
      .map((roleId) => roleNameById.get(roleId))
      .filter((name): name is string => Boolean(name));
  }, [availableRoles, roleIds, sharedRoleNames]);

  return useMemo(() => ({
    isActive,
    roleIds,
    roleNames,
    canMasquerade,
    availableRoles,
    activateMasquerade,
    deactivateMasquerade,
    loading,
  }), [
    isActive,
    roleIds,
    roleNames,
    canMasquerade,
    availableRoles,
    activateMasquerade,
    deactivateMasquerade,
    loading,
  ]);
}
